import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  CircularProgress,
  Typography,
  Switch,
  InputAdornment,
} from '@mui/material';
import Select from 'react-select';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { showSuccessMsg, showErrorMsg } from '../../../utils/toast';
import api from '../../../services/api';
import { useLoadingSpinnerContext } from '../../../context/loadingSpinnerContext';
import Pagination from '../../../components/Pagination';
import ConfirmDialogComponent from '../../../components/ConfirmDialog';
import { styles } from '../../../utils/styles';

const ManageListDialog = ({ open, onClose, fetchInsights }) => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25 });
  const [count, setCount] = useState(0);
  
  const [listSearchQuery, setListSearchQuery] = useState('');
  const [debouncedListSearchQuery, setDebouncedListSearchQuery] = useState('');
  
  // Confirmation Dialog State
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, data: null, newValue: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (open) {
      setListSearchQuery('');
      setSelectedMerchants([]);
      setHasChanges(false);
      setPagination({ page: 1, page_size: 25 });
    }
  }, [open]);

  // Debounce for list search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedListSearchQuery(listSearchQuery);
      setPagination((prev) => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [listSearchQuery]);

  useEffect(() => {
    if (open) {
      loadMonitoredMerchants();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pagination.page, pagination.page_size, debouncedListSearchQuery]);

  const handleClose = () => {
    if (hasChanges && fetchInsights) {
      fetchInsights();
    }
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    let active = true;

    const fetchOptions = async () => {
      try {
        const res = await api.get('merchant/insights/filter_conditions/');
        if (active) {
          if (res && res.status === 200 && Array.isArray(res.data)) {
            setOptions(res.data);
          } else {
            setOptions([]);
          }
        }
      } catch (error) {
        console.error('Failed to fetch merchants:', error);
      }
    };

    if (options.length === 0) {
      fetchOptions();
    }

    return () => {
      active = false;
    };
  }, [open]);

  const loadMonitoredMerchants = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      const res = await api.post('merchant/insights/merchants_list/', {
        page: pagination.page,
        page_size: pagination.page_size,
        search: debouncedListSearchQuery
      });
      
      if (res && res.status === 200) {
        const responseData = res.data.results || res.data.data || res.data;
        setMerchants(Array.isArray(responseData) ? responseData : []);
        setCount(res.data.count || 0);
      } else {
        showErrorMsg(res?.data?.error || 'Failed to load merchants');
        setMerchants([]);
      }
    } catch (error) {
      showErrorMsg('Failed to load merchants');
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPagination({ page: 1, page_size: parseInt(event.target.value, 10) });
  };

  const handleAdd = async () => {
    if (!selectedMerchants || selectedMerchants.length === 0) {
      showErrorMsg('Please select at least one merchant');
      return;
    }

    const newMerchants = selectedMerchants.filter(
      (selected) => !merchants.some((m) => m.merchant_id === selected.merchant_id)
    );

    if (newMerchants.length === 0) {
      showErrorMsg('All selected merchants are already in the monitoring list');
      return;
    }

    try {
      setGlobalLoading(true);
      const payloadMerchants = newMerchants.map(m => ({
        merchant_id: m.merchant_id,
        merchant_name: m.merchant_name
      }));

      const res = await api.post('merchant/insights/create_merchant/', {
        merchants: payloadMerchants,
        valid_flag: true
      });
      setGlobalLoading(false);
      
      if (res && res.status >= 200 && res.status < 300) {
        const insertedCount = res.data?.inserted || newMerchants.length;
        showSuccessMsg(`Added ${insertedCount} merchant(s) to monitoring list`);
        
        loadMonitoredMerchants(false);
        setHasChanges(true);
        setSelectedMerchants([]);
      } else {
        showErrorMsg(res?.data?.error || 'Failed to add merchants');
      }
    } catch (error) {
      setGlobalLoading(false);
      showErrorMsg('An unexpected error occurred');
    }
  };

  const handleUpdate = async (merchant, newValue) => {
    setUpdatingId(merchant.id);
    // Optimistic state update
    setMerchants((prev) => 
      prev.map((m) => m.id === merchant.id ? { ...m, valid_flag: newValue } : m)
    );

    try {
      const res = await api.post('merchant/insights/update_merchant/', {
        id: merchant.id,
        valid_flag: newValue
      });
      
      if (res && res.status >= 200 && res.status < 300) {
        showSuccessMsg('Status updated');
        setHasChanges(true); // Only set flag to reload if API succeeds
      } else {
        throw new Error(res?.data?.error || 'Failed to update status');
      }
    } catch (error) {
      // Revert on failure
      setMerchants((prev) => 
        prev.map((m) => m.id === merchant.id ? { ...m, valid_flag: !newValue } : m)
      );
      showErrorMsg(error.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      action: 'delete',
      data: merchants.find(m => m.id === id),
      newValue: null
    });
  };

  const confirmAction = async () => {
    setActionLoading(true);
    const { data } = confirmDialog;

    try {
      const res = await api.post('merchant/insights/delete_merchant/', { id: data.id });
      
      if (res && res.status >= 200 && res.status < 300) {
        showSuccessMsg('Removed from monitoring list');
        setMerchants(prev => prev.filter(m => m.id !== data.id));
        setCount(prev => prev - 1);
        loadMonitoredMerchants(false);
        setHasChanges(true);
        setConfirmDialog({ open: false, action: null, data: null, newValue: null });
      } else {
        throw new Error(res?.data?.error || 'Failed to remove merchant');
      }
    } catch (error) {
      showErrorMsg(error.message || 'Failed to remove merchant');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Manage Monitored Merchants
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Select
              id="merchant_select"
              name="merchant_select"
              getOptionLabel={(option) => option.label}
              getOptionValue={(option) => option.merchant_id_with_source}
              closeMenuOnSelect={false}
              isMulti
              placeholder="Select Merchant Name"
              options={
                options.filter(item => item && item.merchant_id && item.merchant_name)
                  .map((item) => ({
                    id: item.merchant_id,
                    merchant_id: item.merchant_id,
                    merchant_name: item.merchant_name,
                    label: `${item.merchant_name} (${item.source})`,
                    source: item.source,
                    merchant_id_with_source: item.merchant_id_with_source,
                  })) || []
              }
              value={selectedMerchants}
              onChange={(newValue) => setSelectedMerchants(newValue || [])}
              styles={{
                control: (provided) => ({
                  ...provided,
                  minHeight: '40px',
                  cursor: 'pointer',
                }),
                option: (provided) => ({
                  ...provided,
                  cursor: 'pointer',
                }),
                multiValueRemove: (provided) => ({
                  ...provided,
                  cursor: 'pointer',
                }),
                clearIndicator: (provided) => ({
                  ...provided,
                  cursor: 'pointer',
                }),
                dropdownIndicator: (provided) => ({
                  ...provided,
                  cursor: 'pointer',
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
            />
          </div>
          <Button 
            variant="contained" 
            sx={{ ...styles.customButton, flexShrink: 0, height: '40px' }}
            onClick={handleAdd} 
            disabled={selectedMerchants.length === 0}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
          <Typography variant="h6">
            Currently Monitored ({count})
          </Typography>
          <TextField
            size="small"
            placeholder="Search"
            value={listSearchQuery}
            onChange={(e) => setListSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 200, '& .MuiOutlinedInput-root': { height: '30px', fontSize: '14px' } }}
          />
        </div>

        {loading ? (
          <CircularProgress size={24} sx={{ display: 'block', margin: 'auto', mt: 2, color: styles.customButton.backgroundColor }} />
        ) : (
          <List>
            {merchants.map((m) => {
              const merchantSources = Array.from(new Set(options.filter(o => o.merchant_id === m.merchant_id).map(o => o.source).filter(Boolean)));
              const sourceStr = merchantSources.length > 0 ? ` (${merchantSources.join(', ')})` : '';
              return (
              <ListItem key={m.id}>
                <ListItemText primary={m.merchant_name} secondary={`${m.merchant_id}${sourceStr}`} />
                <ListItemSecondaryAction>
                  <Switch
                    edge="end"
                    checked={m.valid_flag}
                    onChange={(e) => handleUpdate(m, e.target.checked)}
                    disabled={updatingId === m.id}
                    sx={{ 
                      mr: 1,
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: styles.customButton.backgroundColor,
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: styles.customButton.backgroundColor,
                      },
                    }}
                  />
                  <IconButton edge="end" onClick={() => handleDelete(m.id)} disabled={m.valid_flag}>
                    <DeleteIcon color={m.valid_flag ? "disabled" : "error"} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              );
            })}
            {merchants.length === 0 && (
              <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                No merchants are currently being monitored.
              </Typography>
            )}
          </List>
        )}

        {merchants.length > 0 && (
          <div style={{ marginTop: '10px' }}>
            <Pagination
              count={count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>

    <ConfirmDialogComponent
      open={confirmDialog.open} 
      onClose={() => !actionLoading && setConfirmDialog({ ...confirmDialog, open: false })} 
      onSubmit={confirmAction}
      title="Confirm Delete"
      loading={actionLoading}
      maxWidth="xs"
    >
      <Typography>
        {`Are you sure you want to remove ${confirmDialog.data?.merchant_name} from the monitoring list?`}
      </Typography>
    </ConfirmDialogComponent>
    </>
  );
};

export default ManageListDialog;
